import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { buildSearchIndexFromTexts } from 'services/search/SearchIndexService';
import { extractRichTextPlainText } from 'utilities/RichText';
import { getResolvedFields } from 'utilities/NodeType';

const SEARCHABLE_SCHEMA_TYPES = new Set(['string', 'enum', 'barcode', 'richtext']);

const getNodeFieldValueForIndex = (node, field, fallbackTypeKey) => {
  if (!node || !field?.name) return undefined;

  switch (field?.type) {
    case 'cover':
      return node?.coverUrl;
    case 'description':
      return node?.description;
    case 'tags':
      return node?.tags;
    default:
      break;
  }

  const sourceKey = (field.source || fallbackTypeKey || '').toLowerCase();
  const dataKey = sourceKey ? `data_${sourceKey}` : null;

  if (dataKey && node?.[dataKey] && Object.prototype.hasOwnProperty.call(node[dataKey], field.name)) {
    return node[dataKey][field.name];
  }

  return undefined;
};

const appendIndexText = (collector, value) => {
  if (!collector || value == null) return;

  if (Array.isArray(value)) {
    value.forEach((entry) => appendIndexText(collector, entry));
    return;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) collector.push(trimmed);
    return;
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    collector.push(String(value));
    return;
  }

  if (typeof value === 'boolean') {
    collector.push(value ? 'true' : 'false');
    return;
  }

  if (typeof value === 'object') {
    const candidate =
      value.label ??
      value.title ??
      value.name ??
      value.displayName ??
      value.text ??
      value.value ??
      null;

    if (candidate) {
      appendIndexText(collector, candidate);
    }
  }
};

const collectIndexedFieldTexts = (node, nodeTypeDef) => {
  if (!node) return [];

  const typeKey = nodeTypeDef?.key || node.type;
  if (!typeKey) return [];

  const texts = [];
  const fields = getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true });

  fields.forEach((field) => {
    const fieldType = String(field?.type || '').trim().toLowerCase();
    const shouldAutoIndex = SEARCHABLE_SCHEMA_TYPES.has(fieldType);
    if (!shouldAutoIndex && !field?.indexed) return;
    const rawValue = getNodeFieldValueForIndex(node, field, typeKey);
    const indexValue = fieldType === 'richtext'
      ? extractRichTextPlainText(rawValue)
      : rawValue;
    appendIndexText(texts, indexValue);
  });

  return texts;
};

/**
 * Builds canonical node search index from title/tags plus auto-indexed schema fields.
 */
export function buildNodeSearchIndexFromNode(node, nodeTypeDef = null) {
  if (!node || typeof node !== 'object') return [];

  const typeDef = nodeTypeDef || getNodeType(node.type);
  const articleText = node?.article ? extractRichTextPlainText(node.article) : '';
  const titleText = String(node.title || '').trim();
  const tagsText = Array.isArray(node.tags) ? node.tags : [];
  const indexedFieldTexts = collectIndexedFieldTexts(node, typeDef);

  return buildSearchIndexFromTexts([
    articleText,
    titleText,
    tagsText,
    indexedFieldTexts,
  ]);
}